import {
  useBoundProp,
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';

import { Checkbox as CheckboxPrimitive } from '@/components/ui/Checkbox';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { FieldError, PillField } from '../field';
import { fieldErrorId, useElementId } from '../fieldIds';
import {
  HELD_CLASS,
  PILL_CLASS,
  PILL_ERROR_CLASS,
  sizingClass,
  TEXT_STYLE,
} from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

export const Checkbox: ComponentFn<typeof catalog, 'Checkbox'> = ({
  props,
  bindings,
}) => {
  const id = useElementId(props);
  const [checked, setChecked] = useBoundProp<boolean>(
    props.checked,
    bindings?.checked
  );
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const path = bindings?.checked;
  const held = status === 'running';
  // A literal value has no state path to write to, so the box cannot be ticked
  const editable =
    path !== undefined &&
    props.editable !== false &&
    !session.readOnly &&
    !held;
  const error = props.error;
  const appearance = props.appearance ?? 'square';

  if (status === 'completed' || status === 'completed_externally') {
    if (!checked) return null;
    return (
      <span className={cn('text-foreground-subtle', TEXT_STYLE.note)}>
        {props.label}
      </span>
    );
  }

  const toggle = (next: boolean) => {
    setChecked(next);
    controls.commitNow();
  };

  const onFocus = () => {
    if (path) controls.focus(path);
  };
  const onBlur = () => {
    if (path) controls.blur(path);
  };

  const control =
    appearance === 'pill' ? (
      <button
        id={id}
        type="button"
        role="checkbox"
        aria-checked={checked === true}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? fieldErrorId(id) : undefined}
        disabled={!editable}
        className={cn(
          PILL_CLASS,
          'w-fit',
          !checked && 'text-foreground-muted',
          error && PILL_ERROR_CLASS
        )}
        title={props.label}
        onClick={() => toggle(!checked)}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <span
          aria-hidden="true"
          className={cn(
            'h-[calc(10*var(--ac-u))] w-[calc(18*var(--ac-u))] shrink-0 rounded-full p-[calc(2*var(--ac-u))]',
            checked ? 'bg-kind-act' : 'bg-secondary'
          )}
        >
          <span
            className={cn(
              'block size-[calc(6*var(--ac-u))] rounded-full bg-surface',
              checked && 'translate-x-[calc(8*var(--ac-u))]'
            )}
          />
        </span>
        <span className="min-w-0 truncate">{props.label}</span>
      </button>
    ) : (
      <XStack className="min-w-0 items-center gap-[var(--ac-gap-sm)]">
        <CheckboxPrimitive
          id={id}
          checked={checked === true}
          disabled={!editable}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? fieldErrorId(id) : undefined}
          onCheckedChange={(next) => toggle(next === true)}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <label
          htmlFor={id}
          className={cn(
            TEXT_STYLE.body,
            editable ? 'text-foreground' : 'text-foreground-subtle',
            error && 'text-danger-11'
          )}
        >
          {props.label}
        </label>
      </XStack>
    );

  return (
    <YStack
      className={cn(
        'min-w-0 gap-[var(--ac-gap-xs)]',
        sizingClass(props.sizing),
        held && HELD_CLASS
      )}
    >
      {appearance === 'pill' ? (
        <PillField id={id} error={error}>
          {control}
        </PillField>
      ) : (
        <>
          {control}
          <FieldError id={id} error={error} />
        </>
      )}
    </YStack>
  );
};
